import type { MapDispatchToPropsFunction, MapStateToProps } from 'react-redux';
import { connect } from 'react-redux';
import { ordersBasketFormSelectionToggled } from 'state/orders-basket/actions';
import {
  getOrdersBasketDataByFormId,
  getOrdersBasketExposedFormStatusById,
  getOrdersBasketFieldsMetadata,
  getOrdersBasketFormErrorsById,
  getOrdersBasketFormJsonRepresentation,
  getOrdersBasketFormQuoteWarnings,
  getOrdersBasketFormStatusById,
  getOrdersBasketIsEditable,
  getOrdersBasketValidationErrors,
  isOrdersBasketFormSelectable,
  isOrdersBasketFormSelected,
} from 'state/orders-basket/selectors';
import { ordersBasketFormValueChangedThunk } from 'state/orders-basket/thunks';
import type { ThunkDispatch } from 'state/state.common';
import type { AppState } from 'state/state.model';
import { getVisibleOrdersBasketProductDefinitionsByProductVariation } from 'state/workspaces/ordersBasket/productDefinitions/productDefinitions.selectors';

import type {
  OrdersBasketFormDispatchProps,
  OrdersBasketFormOwnProps,
  OrdersBasketFormStateProps,
} from './OrdersBasketForm.models';

export const mapStateToProps: MapStateToProps<OrdersBasketFormStateProps, OrdersBasketFormOwnProps, AppState> = (
  state: AppState,
  ownProps: OrdersBasketFormOwnProps,
) => {
  const productView = 'OrdersBasket';
  const productDefinition = getVisibleOrdersBasketProductDefinitionsByProductVariation(state, {
    productType: ownProps.productType,
    productSubType: ownProps.productSubType,
  });
  const status = getOrdersBasketFormStatusById(state, ownProps.formId);
  const exposedStatus = getOrdersBasketExposedFormStatusById(state, ownProps.formId);
  const formJsonRepresentation =
    ownProps.formId !== undefined && (status === 'REJECTED' || status === 'EXECUTED_WITH_WARNING')
      ? getOrdersBasketFormJsonRepresentation(state, ownProps.formId)
      : undefined;
  const formData = getOrdersBasketDataByFormId(state, ownProps.formId);
  const fieldsMetadata = getOrdersBasketFieldsMetadata(state, ownProps.formId);
  const isNotEditable = !getOrdersBasketIsEditable(state, ownProps.formId);

  return {
    productView,
    productDefinition: productDefinition !== undefined ? productDefinition[0] : undefined,
    exposedStatus,
    isSelected: isOrdersBasketFormSelected(state, ownProps.formId),
    canBeSelected: isOrdersBasketFormSelectable(state, ownProps.formId),
    errors: status === 'REJECTED' ? getOrdersBasketFormErrorsById(state, ownProps.formId) : undefined,
    warnings: status === 'EXECUTED_WITH_WARNING' ? getOrdersBasketFormQuoteWarnings(state, ownProps.formId) : undefined,
    validationErrors: status === 'NOT_READY' ? getOrdersBasketValidationErrors(state, ownProps.formId, 2) : [],
    formJsonRepresentation,
    formData,
    fieldsMetadata,
    isNotEditable,
  };
};

export const mapDispatchToProps: MapDispatchToPropsFunction<OrdersBasketFormDispatchProps, OrdersBasketFormOwnProps> = (
  dispatch: ThunkDispatch,
  ownProps: OrdersBasketFormOwnProps,
) => ({
  onCheckboxToggled() {
    dispatch(ordersBasketFormSelectionToggled(ownProps.formId));
  },
  setFieldValue(fieldId: string, value: unknown) {
    dispatch(ordersBasketFormValueChangedThunk(ownProps.formId, fieldId, value as Readonly<unknown>));
  },
});

export const connectOrdersBasketFormComponent = connect<
  OrdersBasketFormStateProps,
  OrdersBasketFormDispatchProps,
  OrdersBasketFormOwnProps,
  AppState
>(mapStateToProps, mapDispatchToProps);
